// src/components/heygen/HeyGenStudioPage.tsx
// Multi-scene studio editor — assemble scenes and generate a composed video.

import { useCallback, useEffect, useState } from 'react';
import {
  ChevronDown, ChevronUp, Loader2, Plus, Trash2, Video,
} from 'lucide-react';

import {
  ApiError, listAvatars, listVoices, generateStudio, checkJob,
  type HeyGenAvatar, type HeyGenVoice, type HeyGenJob,
} from '@/lib/api_heygen';
import { useJobPoller } from '@/hooks/useJobPoller';
import { VideoPlayer } from '@/components/shared/VideoPlayer';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { ApiKeyMissing } from '@/components/shared/ApiKeyMissing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

interface SceneRow {
  _key: number;
  avatar_id: string;
  voice_id: string;
  script: string;
  background: string;
}

let keySeq = 0;
function newScene(avatarId: string, voiceId: string): SceneRow {
  return { _key: ++keySeq, avatar_id: avatarId, voice_id: voiceId, script: '', background: '#0D0F1A' };
}

export function HeyGenStudioPage({ addNotification }: { addNotification?: Notifier }) {
  const [avatars, setAvatars] = useState<HeyGenAvatar[]>([]);
  const [voices, setVoices] = useState<HeyGenVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiMissing, setApiMissing] = useState(false);

  const [scenes, setScenes] = useState<SceneRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setApiMissing(false);
    try {
      const [a, v] = await Promise.all([listAvatars(), listVoices()]);
      setAvatars(a);
      setVoices(v);
      if (scenes.length === 0 && a.length && v.length) {
        setScenes([newScene(a[0].avatar_id, v[0].voice_id)]);
      }
    } catch (e: any) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 502)) setApiMissing(true);
      else addNotification?.({ type: 'error', title: 'Error', message: e.message });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const poller = useJobPoller<HeyGenJob>({
    checkFn: checkJob,
    jobId,
    interval: 4000,
    onComplete: (r: HeyGenJob) => {
      setVideoUrl(r.video_url || null);
      addNotification?.({ type: 'success', title: 'Video Studio generado' });
    },
    onFail: (r: HeyGenJob) => {
      addNotification?.({ type: 'error', title: 'Error en Studio', message: r.error_message || 'Failed' });
    },
  });

  const updateScene = (key: number, patch: Partial<SceneRow>) => {
    setScenes((prev) => prev.map((s) => (s._key === key ? { ...s, ...patch } : s)));
  };

  const removeScene = (key: number) => {
    setScenes((prev) => prev.filter((s) => s._key !== key));
  };

  const moveScene = (key: number, dir: -1 | 1) => {
    setScenes((prev) => {
      const idx = prev.findIndex((s) => s._key === key);
      if (idx < 0) return prev;
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const handleSubmit = async () => {
    const valid = scenes.filter((s) => s.script.trim());
    if (!valid.length) return;
    setSubmitting(true);
    setVideoUrl(null);
    setJobId(null);
    try {
      const payload = valid.map(({ _key, ...rest }) => rest);
      const job = await generateStudio({ scenes: payload });
      setJobId(job.id);
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error creando Studio', message: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (apiMissing) return <ApiKeyMissing service="HeyGen" />;
  if (loading) return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-white/40" /></div>;

  const isPolling = poller.status === 'polling';

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="flex items-center justify-between">
        <h2 className="text-sm text-white/70 font-medium">Escenas ({scenes.length})</h2>
        <button
          onClick={() => setScenes((p) => [...p, newScene(avatars[0]?.avatar_id || '', voices[0]?.voice_id || '')])}
          className="flex items-center gap-1 text-xs text-[var(--acc1)] hover:text-[var(--acc1)]/80"
        >
          <Plus size={14} /> Añadir escena
        </button>
      </div>

      {scenes.map((scene, idx) => (
        <div key={scene._key} className="o-card border border-white/10 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/40 font-medium">Escena {idx + 1}</span>
            <div className="flex gap-1">
              <button onClick={() => moveScene(scene._key, -1)} className="p-1 text-white/30 hover:text-white/60" disabled={idx === 0}><ChevronUp size={14} /></button>
              <button onClick={() => moveScene(scene._key, 1)} className="p-1 text-white/30 hover:text-white/60" disabled={idx === scenes.length - 1}><ChevronDown size={14} /></button>
              <button onClick={() => removeScene(scene._key)} className="p-1 text-red-400/50 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Avatar</label>
              <select
                value={scene.avatar_id}
                onChange={(e) => updateScene(scene._key, { avatar_id: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
              >
                {avatars.map((a) => <option key={a.avatar_id} value={a.avatar_id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Voz</label>
              <select
                value={scene.voice_id}
                onChange={(e) => updateScene(scene._key, { voice_id: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
              >
                {voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-white/50 mb-1 block">Script</label>
            <textarea
              value={scene.script}
              onChange={(e) => updateScene(scene._key, { script: e.target.value })}
              rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)] resize-y"
              placeholder="Texto que dirá el avatar en esta escena…"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs text-white/50">Background</label>
            <input
              type="color"
              value={scene.background || '#0D0F1A'}
              onChange={(e) => updateScene(scene._key, { background: e.target.value })}
              className="w-8 h-6 rounded cursor-pointer bg-transparent border border-white/10"
            />
          </div>
        </div>
      ))}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting || isPolling || !scenes.some((s) => s.script.trim())}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {submitting || isPolling ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />}
        {isPolling ? 'Generando…' : 'Generar video Studio'}
      </button>

      {/* Status */}
      {jobId && poller.status !== 'idle' && (
        <div className="o-card flex items-center gap-3">
          <JobStatusBadge status={poller.data?.status || 'pending'} />
          <span className="text-sm text-white/60">
            {poller.status === 'polling' && 'Procesando…'}
            {poller.status === 'completed' && 'Video listo'}
            {poller.status === 'failed' && (poller.error || 'Error')}
          </span>
        </div>
      )}

      {videoUrl && (
        <div className="o-card border border-emerald-500/30">
          <VideoPlayer src={videoUrl} />
        </div>
      )}
    </div>
  );
}
